import { useState, useEffect, useMemo, useRef, Suspense } from "react";
import { lazyWithRetry } from "../lib/lazyWithRetry.js";
import { X, Pencil, Trash2, Eye, Download, Check, Upload } from "lucide-react";
import { api, getAccessToken, errMsg } from "../lib/api.js";
import { logError } from "../lib/logger.js";
import { useLookup } from "../context/LookupContext.js";
import { usePageBack } from "../context/PageBackContext.js";
import { useMe } from "../context/MeContext.js";
import { ManagedInADP } from "../components/ManagedInADP.js";
import { DatePicker } from "../components/DatePicker.js";
import { DateInput } from "../components/ui/date-input.js";
import { DropdownSelect, DropdownButton, DropdownPanel, DropdownCheckboxRow } from "../components/DropdownButton.js";
import { MenuList } from "../components/ui/menu-list.js";
import { Combobox } from "../components/ui/combobox.js";
import { cn } from "../lib/utils.js";
import { Input } from "../components/ui/input.js";
import { Label } from "../components/ui/label.js";
import { Checkbox } from "../components/ui/checkbox.js";
import { Textarea } from "../components/ui/textarea.js";
import { Badge, type BadgeProps } from "../components/ui/badge.js";
import { Button } from "../components/ui/button.js";
import { AsyncButton } from "../components/ui/async-button.js";
import { SpecularButton } from "../components/ui/specular-button.js";
import { Pagination } from "../components/ui/pagination.js";
import { GlowCard } from "../components/ui/glow-card.js";
import { PanelReveal } from "../components/ui/panel-reveal.js";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs.js";
import { Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle } from "../components/ui/dialog.js";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../components/ui/table.js";
import { useConfirm, confirmDialogDefaults } from "../components/ui/confirm-dialog.js";
import { ConfirmationCard } from "../components/ui/confirmation-card.js";
// Lazy: the Report tab is the only thing that pulls in Recharts, and most
// visits never open it. Loading it on demand keeps it out of the entry chunk --
// the same reason EmployeeSummaryCharts is split out.
const ExpenseReportAnalytics = lazyWithRetry(() => import("./expenses/ExpenseReportAnalytics.js"));
const PettyCashBook = lazyWithRetry(() => import("./expenses/PettyCashBook.js"));
import { FileInput } from "../components/ui/file-input.js";

import { HR_ROLES, MGR_ROLES, FINANCE_ROLES, EXPENSE_ANALYTICS_ROLES, PETTY_CASH_ROLES, PETTY_CASH_ADMIN_ROLES } from "@hrms/types";

function decodeRole(token: string): string {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]!));
    return (payload as Record<string, unknown>)["role"] as string ?? "";
  } catch { return ""; }
}

interface ExpenseReport {
  id: string; title: string; status: string; currency: string; totalAmount: string;
  createdAt: string; approvedAt: string | null; financeApprovedAt: string | null;
  remark: string | null;
  approvalDueAt: string | null; escalatedAt: string | null; managerApprovedLate: boolean;
  employee: { id: string; firstName: string; lastName: string; employeeCode: string };
  _count: { items: number };
}
interface ExpenseItem {
  id: string; category: string; description: string; amount: string; currency: string;
  expenseDate: string; billableToClient: boolean; billableMarkup: string | null; receiptS3Key: string | null;
  receiptNumber: string | null;
  noReceipt: boolean; noReceiptReason: string | null; financeVerified: boolean; managerVerified: boolean;
  /** PETTY_CASH_MODE value; null on items added before payment mode existed. */
  paymentMode: string | null;
  /** Set by Finance while verifying; once set it is what is paid (approvedAmount ?? amount). */
  approvedAmount: string | null;
}
interface ReportDetail extends Omit<ExpenseReport, "_count"> {
  items: ExpenseItem[];
}

function formatDateMDY(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split("-");
  return `${m}-${d}-${y}`;
}

// A full timestamp (e.g. createdAt) as MM-DD-YYYY in the viewer's local time, so a
// report created early morning IST doesn't show the previous UTC day.
function formatTimestampMDY(iso: string): string {
  const dt = new Date(iso);
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const dd = String(dt.getDate()).padStart(2, "0");
  return `${mm}-${dd}-${dt.getFullYear()}`;
}

const statusTone = (s: string): BadgeProps["tone"] => {
  if (s === "FINANCE_APPROVED" || s === "PAID") return "success";
  if (s === "MANAGER_APPROVED") return "info";
  if (s === "SUBMITTED") return "warning";
  if (s === "REJECTED") return "error";
  return "neutral";
};

const inputStyle: React.CSSProperties = {
  padding: "8px 10px", border: "1px solid var(--color-surface-200)",
  borderRadius: "var(--radius-md)", fontSize: "14px", background: "var(--color-surface-0)", width: "100%",
};

const BLANK_ITEM = { category: "TRAVEL", paymentMode: "", approvedAmount: "", description: "", amount: "", currency: "INR", expenseDate: "", billableToClient: false, billableMarkup: "", noReceipt: false, noReceiptReason: "", receiptNumber: "" };

/** What an item pays: Finance's approved amount once set, else the claim. */
const payable = (i: Pick<ExpenseItem, "amount" | "approvedAmount">) => Number(i.approvedAmount ?? i.amount);

const BLANK_REPORT = { title: "", currency: "INR" };

// First day of the current quarter (Jan–Mar, Apr–Jun, Jul–Sep, Oct–Dec) as
// YYYY-MM-DD — expense items can't be dated before it. Mirrors the server's
// expenses.quarter lock.
function currentQuarterStartISO(): string {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth(); // 0-11
  const startMonth = Math.floor(m / 3) * 3; // 0,3,6,9
  const mm = String(startMonth + 1).padStart(2, "0");
  return `${y}-${mm}-01`;
}

// from/to for a period selection. Month uses the real last day so February and
// 30/31-day months are exact. Shared by the table filter and the export dialog.
function periodRange(period: string, year: string, month: string, from: string, to: string): { from?: string; to?: string } {
  if (period === "year") return { from: `${year}-01-01`, to: `${year}-12-31` };
  if (period === "month") {
    const lastDay = new Date(Number(year), Number(month), 0).getDate();
    return { from: `${year}-${month}-01`, to: `${year}-${month}-${String(lastDay).padStart(2, "0")}` };
  }
  if (period === "custom") return { ...(from ? { from } : {}), ...(to ? { to } : {}) };
  return {};
}

// Each status names the step it is waiting on, so a manager-approved report never
// reads as finally approved.
const STATUS_LABEL: Record<string, string> = {
  DRAFT: "Draft",
  SUBMITTED: "Pending Manager Approval",
  MANAGER_APPROVED: "Pending Finance Approval",
  FINANCE_APPROVED: "Finance Approved",
  PAID: "Paid",
  REJECTED: "Rejected",
};
const statusLabel = (s: string) => STATUS_LABEL[s] ?? s.replace(/_/g, " ");

const EXPENSE_STATUS_OPTIONS = ["DRAFT", "SUBMITTED", "MANAGER_APPROVED", "FINANCE_APPROVED", "REJECTED"]
  .map(s => ({ value: s, label: statusLabel(s) }));
// The "Pending" sub-tab: reports still in the approval pipeline (manager or finance).
const PENDING_STATUSES = ["SUBMITTED", "MANAGER_APPROVED"];
const CURRENCY_OPTIONS = ["INR", "USD"].map(c => ({ value: c, label: c }));

// Statuses offered as checkboxes in the export dialog (drafts aren't exported).
const EXPORT_STATUS_OPTIONS = ["SUBMITTED", "MANAGER_APPROVED", "FINANCE_APPROVED", "PAID", "REJECTED"]
  .map(s => ({ value: s, label: statusLabel(s) }));

const EXPORT_PERIOD_OPTIONS = [
  { value: "all", label: "All time" },
  { value: "month", label: "Specific month" },
  { value: "year", label: "Specific year" },
  { value: "custom", label: "Custom range" },
];
const EXPORT_YEAR_OPTIONS = Array.from({ length: 6 }, (_, i) => {
  const y = String(new Date().getFullYear() - i);
  return { value: y, label: y };
});
const EXPORT_MONTH_OPTIONS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"]
  .map((label, i) => ({ value: String(i + 1).padStart(2, "0"), label }));

export default function ExpensesPage() {
  usePageBack("/dashboard");
  const { isUS, employeeId } = useMe();
  const confirm = useConfirm();
  const expenseCategories = useLookup("EXPENSE_CATEGORY");
  const expenseCategoryOptions = useMemo(
    () => expenseCategories.map(c => ({ value: c.value, label: c.displayName })),
    [expenseCategories]
  );
  // One "Expense Type" list (shared with the Petty Cash Book) and its payment modes.
  const paymentModes = useLookup("PETTY_CASH_MODE");
  const paymentModeOptions = useMemo(
    () => paymentModes.map(m => ({ value: m.value, label: m.displayName })),
    [paymentModes]
  );
  const expenseTypeLabel = (v: string) => expenseCategories.find(c => c.value === v)?.displayName ?? v;
  const paymentModeLabel = (v: string | null) => (v ? paymentModes.find(m => m.value === v)?.displayName ?? v : "—");
  const accessToken = getAccessToken() ?? "";
  const userRole = decodeRole(accessToken);
  const isHR = HR_ROLES.has(userRole);
  const isMgr = MGR_ROLES.has(userRole);
  const isFinance = FINANCE_ROLES.has(userRole);
  const canApproveManager = isHR || isMgr;
  const canApproveFinance = isFinance;
  const canViewTeam = canApproveManager || canApproveFinance;
  const canViewReport = EXPENSE_ANALYTICS_ROLES.has(userRole);
  const canViewPettyCash = PETTY_CASH_ROLES.has(userRole);

  const [tab, setTab] = useState<"mine" | "team" | "report" | "pettycash">(
    canViewTeam ? "team" : PETTY_CASH_ADMIN_ROLES.has(userRole) ? "pettycash" : "mine"
  );
  // My Expense / Team Expense open on "Pending"; "All" shows every status.
  const [view, setView] = useState<"pending" | "all">("pending");
  const [reports, setReports] = useState<ExpenseReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatuses, setFilterStatuses] = useState<Set<string>>(new Set());
  const statusParam = useMemo(() => Array.from(filterStatuses).sort().join(","), [filterStatuses]);
  // One "which dropdown is open" flag for the page's filter bar.
  const [openFilter, setOpenFilter] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const searchRef = useRef<ReturnType<typeof setTimeout>>();
  const [editingReportId, setEditingReportId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ total: 0, page: 1, limit: 20, totalPages: 1 });
  const [selected, setSelected] = useState<ReportDetail | null>(null);
  // The Petty Cash Book lists admins' report items and can open their report here;
  // closing the modal refreshes the book, so an approve or reject made in it shows.
  const [pettyCashRefresh, setPettyCashRefresh] = useState(0);
  useEffect(() => { if (!selected) setPettyCashRefresh(k => k + 1); }, [selected]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showNewReport, setShowNewReport] = useState(false);
  const [showAddItem, setShowAddItem] = useState(false);
  const addItemPanelRef = useRef<HTMLDivElement | null>(null);
  const verifyPanelRef = useRef<HTMLDivElement | null>(null);
  const [editingItem, setEditingItem] = useState<ExpenseItem | null>(null);
  const [actionDialog, setActionDialog] = useState<"manager" | "finance" | "reject" | null>(null);
  const [remarkInput, setRemarkInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [newReportForm, setNewReportForm] = useState({ ...BLANK_REPORT });
  const [showExport, setShowExport] = useState(false);
  const [exportEmployeeId, setExportEmployeeId] = useState("");
  const [exportStatuses, setExportStatuses] = useState<Set<string>>(new Set());
  // Employee directory (id/name/code/email), loaded once and shared by the team
  // table filter and the export dialog.
  const [employees, setEmployees] = useState<{ id: string; name: string; code: string; email: string }[]>([]);
  // Export period: all time, a single month, a full year, or a custom range.
  const [exportPeriod, setExportPeriod] = useState<"all" | "month" | "year" | "custom">("all");
  const [exportYear, setExportYear] = useState(String(new Date().getFullYear()));
  const [exportMonth, setExportMonth] = useState(String(new Date().getMonth() + 1).padStart(2, "0"));
  const [exportFrom, setExportFrom] = useState("");
  const [exportTo, setExportTo] = useState("");
  // Team table filters (employee + period), separate from the export selection.
  const [filterEmployeeId, setFilterEmployeeId] = useState("");
  const [filterPeriod, setFilterPeriod] = useState<"all" | "month" | "year" | "custom">("all");
  const [filterYear, setFilterYear] = useState(String(new Date().getFullYear()));
  const [filterMonth, setFilterMonth] = useState(String(new Date().getMonth() + 1).padStart(2, "0"));
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");
  const { from: fromParam, to: toParam } = useMemo(
    () => periodRange(filterPeriod, filterYear, filterMonth, filterFrom, filterTo),
    [filterPeriod, filterYear, filterMonth, filterFrom, filterTo]
  );
  const [itemForm, setItemForm] = useState({ ...BLANK_ITEM });
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [pendingReceipt, setPendingReceipt] = useState<File | null>(null);
  const [parsingReceipt, setParsingReceipt] = useState(false);
  const [receiptInfo, setReceiptInfo] = useState("");
  const [uploadingReceipt, setUploadingReceipt] = useState<string | null>(null);
  const [verifyQueue, setVerifyQueue] = useState<ExpenseItem[]>([]);
  const [verifyReviewItem, setVerifyReviewItem] = useState<ExpenseItem | null>(null);
  const [verifyKind, setVerifyKind] = useState<"manager" | "finance" | null>(null);
  const [verifyChecked, setVerifyChecked] = useState(false);
  const [verifyReceiptUrl, setVerifyReceiptUrl] = useState<string | null>(null);
  const [verifySaving, setVerifySaving] = useState(false);
  // Finance verify: the amount it approves for the item (prefilled with the claim).
  const [verifyApproved, setVerifyApproved] = useState("");

  // `employeeId` is "" until MeContext loads -- treat unknown identity as own, so approval buttons stay hidden.
  const isOwnReport = !employeeId || selected?.employee.id === employeeId;
  const isPrivilegedApprover = isHR || isFinance;
  const canEditItems = isHR || isFinance
    || (isOwnReport && (selected?.status === "DRAFT" || selected?.status === "REJECTED"))
    || (isMgr && !isOwnReport && selected?.status === "SUBMITTED");
  const canManagerVerify = (isHR || isMgr) && (!isOwnReport || isPrivilegedApprover) && selected?.status === "SUBMITTED";
  const showManagerVerifyColumn = (isHR || isMgr || isFinance || isOwnReport) && selected?.status === "SUBMITTED";
  const showFinanceVerifyColumn = isFinance || isOwnReport;
  // totalAmount is the payable total; the claimed sum shows beside it when Finance approved less.
  const claimedTotal = Math.round((selected?.items ?? []).reduce((t, i) => t + Number(i.amount), 0) * 100) / 100;
  const approvedEditable = isFinance && !!editingItem;

  // `searchOverride` is passed by the debounced search so the request uses the value
  // the user actually typed, not a stale `search` captured in the timer's closure —
  // without it, clearing the box re-ran the fetch with the previous term and never
  // showed everything again.
  async function load(pageOverride?: number, searchOverride?: string) {
    setLoading(true);
    try {
      const s = searchOverride ?? search;
      const params = new URLSearchParams({ page: String(pageOverride ?? page), limit: "20", scope: tab });
      // Pending with no status ticked means both pending statuses; the status menu only
      // offers those two there, so a ticked subset is already pending.
      const status = view === "pending" && !statusParam ? PENDING_STATUSES.join(",") : statusParam;
      if (status) params.set("status", status);
      if (s) params.set("search", s);
      if (tab === "team" && filterEmployeeId) params.set("employeeId", filterEmployeeId);
      if (fromParam) params.set("from", fromParam);
      if (toParam) params.set("to", toParam);
      const res = await api.get<{ data: ExpenseReport[]; meta: typeof meta }>(`/expenses?${params}`);
      setReports(res.data.data);
      setMeta(res.data.meta);
    } catch (err) { logError("ExpensesPage.load", err); } finally { setLoading(false); }
  }

  // The team view never shows drafts, so Draft isn't an option there; Pending offers only its two statuses.
  const statusFilterOptions = view === "pending"
    ? EXPENSE_STATUS_OPTIONS.filter(o => PENDING_STATUSES.includes(o.value))
    : tab === "team"
      ? EXPENSE_STATUS_OPTIONS.filter(o => o.value !== "DRAFT")
      : EXPENSE_STATUS_OPTIONS;

  function toggleFilterStatus(v: string) {
    setFilterStatuses(prev => {
      const next = new Set(prev);
      if (next.has(v)) next.delete(v); else next.add(v);
      return next;
    });
    setPage(1);
  }

  function onSearchChange(v: string) {
    setSearch(v);
    setPage(1);
    clearTimeout(searchRef.current);
    searchRef.current = setTimeout(() => load(1, v), 300);
  }

  // The Report tab is a self-contained analytics view; it must not trigger the
  // list fetch. Also defend against a stale "report" selection for a user who
  // can't view it (e.g. after a role change).
  useEffect(() => {
    if (tab === "report" && !canViewReport) { setTab(canViewTeam ? "team" : "mine"); return; }
    if (tab === "pettycash" && !canViewPettyCash) { setTab(canViewTeam ? "team" : "mine"); return; }
    if (tab === "report" || tab === "pettycash") return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusParam, page, tab, view, filterEmployeeId, fromParam, toParam]);

  // HR/Finance get an employee filter on the team tab — load the directory for it.
  useEffect(() => {
    if (tab === "team" && (isHR || isFinance)) void loadEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // The item form renders under the item list, inside the modal's own scroll
  // container -- with a long list it opens off-screen, so the click looks like
  // it did nothing. Bring it into view once it has rendered.
  useEffect(() => {
    if (!showAddItem) return;
    const frame = requestAnimationFrame(() => {
      addItemPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    return () => cancelAnimationFrame(frame);
  }, [showAddItem, editingItem]);

  async function openReport(id: string) {
    setDetailLoading(true);
    try {
      const res = await api.get<ReportDetail>(`/expenses/${id}`);
      setSelected(res.data);
    } catch (err) { logError("ExpensesPage.openReport", err); } finally { setDetailLoading(false); }
  }

  async function saveReport() {
    setSaving(true); setError("");
    try {
      if (editingReportId) {
        await api.patch(`/expenses/${editingReportId}`, newReportForm);
      } else {
        await api.post("/expenses", newReportForm);
      }
      setShowNewReport(false);
      setEditingReportId(null);
      setNewReportForm({ ...BLANK_REPORT });
      load();
    } catch (e: unknown) {
      setError(errMsg(e) ?? "Failed");
      throw e;
    } finally { setSaving(false); }
  }

  async function viewReceipt(itemId: string) {
    try {
      const r = await api.get<{ url: string }>(`/expenses/items/${itemId}/receipt`);
      window.open(r.data.url, "_blank");
    } catch (e: unknown) {
      alert(errMsg(e) ?? "Failed to load receipt");
    }
  }

  async function downloadReceipt(itemId: string) {
    try {
      const res = await api.get(`/expenses/items/${itemId}/receipt/download`, { responseType: "blob" });
      const disposition = res.headers?.["content-disposition"] as string | undefined;
      const filename = disposition?.match(/filename="([^"]+)"/)?.[1] ?? "receipt";
      const url = URL.createObjectURL(res.data as Blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e: unknown) {
      alert(errMsg(e) ?? "Failed to download receipt");
    }
  }

  async function loadEmployees() {
    if (employees.length > 0) return;
    try {
      const res = await api.get<Array<{ id: string; firstName: string; lastName: string; employeeCode: string; email: string }>>("/employees/org-chart");
      setEmployees(res.data.map(e => ({ id: e.id, name: `${e.firstName} ${e.lastName}`, code: e.employeeCode, email: e.email })));
    } catch (e: unknown) { logError("ExpensesPage.loadEmployees", e); }
  }

  async function openExportDialog() {
    setExportEmployeeId("");
    setExportStatuses(new Set());
    setExportPeriod("all");
    setExportFrom("");
    setExportTo("");
    setShowExport(true);
    void loadEmployees();
  }

  // Employee options for the searchable pickers (table filter + export dialog).
  const employeeOptions = useMemo(
    () => [{ value: "", label: "All employees" }, ...employees.map(e => ({ value: e.id, label: e.name, sub: `${e.code} · ${e.email}` }))],
    [employees]
  );

  function toggleExportStatus(v: string) {
    setExportStatuses(prev => {
      const next = new Set(prev);
      if (next.has(v)) next.delete(v); else next.add(v);
      return next;
    });
  }

  // The from/to dates for the chosen period. Month uses the real last day so
  // February and 30/31-day months are exact.
  function resolveExportRange(): { from?: string; to?: string } {
    return periodRange(exportPeriod, exportYear, exportMonth, exportFrom, exportTo);
  }

  // Downloads the reimbursement detail for the chosen employee + statuses + period.
  // Rethrows so the AsyncButton shows the failure instead of a false ✓.
  async function downloadReimbursementExcel() {
    try {
      const params = new URLSearchParams();
      if (exportEmployeeId) params.set("employeeId", exportEmployeeId);
      if (exportStatuses.size) params.set("status", Array.from(exportStatuses).join(","));
      const { from, to } = resolveExportRange();
      if (from) params.set("from", from);
      if (to) params.set("to", to);
      const res = await api.get(`/expenses/export?${params}`, { responseType: "blob" });
      const disposition = res.headers?.["content-disposition"] as string | undefined;
      const filename = disposition?.match(/filename="([^"]+)"/)?.[1] ?? "reimbursement-detail.xlsx";
      const url = URL.createObjectURL(res.data as Blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      setShowExport(false);
    } catch (e: unknown) {
      alert(errMsg(e) ?? "Failed to download Excel");
      throw e;
    }
  }

  async function uploadReceipt(reportId: string, itemId: string, file: File) {
    setUploadingReceipt(itemId);
    try {
      const r = await api.post<{ uploadUrl: string; key: string }>(
        `/expenses/${reportId}/items/${itemId}/receipt`,
        { filename: file.name, mimeType: file.type }
      );
      const uploadRes = await fetch(r.data.uploadUrl, { method: "PUT", body: file, headers: { "Content-Type": file.type } });
      if (!uploadRes.ok) throw new Error("S3 upload failed");
    } catch (e: unknown) {
      setError(errMsg(e) ?? "Failed to upload receipt");
    } finally { setUploadingReceipt(null); }
  }

  // AI-reads the chosen receipt and pre-fills the item fields the user then reviews
  // and saves — mirrors the employee document auto-fill. Currency stays locked to the
  // report's currency (the field is disabled), so it is never overwritten here.
  const EXPENSE_CATS = ["TRAVEL", "MEALS", "ACCOMMODATION", "EQUIPMENT", "TRAINING", "OTHER"];
  async function autofillFromReceipt(file?: File) {
    const rf = file ?? receiptFile;
    if (!rf) { setError("Choose a receipt file first."); throw new Error("validation"); }
    setError("");
    try {
      const fd = new FormData();
      // Send the report's currency first so the server converts a receipt in the
      // other currency to it before returning the amount.
      if (selected) fd.append("targetCurrency", selected.currency);
      fd.append("file", rf);
      const res = await api.post<{ fields: Record<string, string | null> }>("/ai/parse-receipt", fd);
      const f = res.data.fields ?? {};
      const cat = typeof f["category"] === "string" ? f["category"].toUpperCase() : "";
      const amtNum = f["amount"] != null ? parseFloat(String(f["amount"])) : NaN;
      const date = String(f["expenseDate"] ?? "");
      setReceiptInfo(
        f["originalCurrency"] && f["originalAmount"]
          ? `Receipt was in ${f["originalCurrency"]} ${f["originalAmount"]}; converted to ${selected?.currency} at 1 USD = ₹${f["conversionRate"]}.`
          : ""
      );
      setItemForm(prev => ({
        ...prev,
        category: EXPENSE_CATS.includes(cat) ? cat : prev.category,
        description: (f["description"] || f["merchant"] || prev.description) as string,
        amount: Number.isFinite(amtNum) ? String(amtNum) : prev.amount,
        expenseDate: /^\d{4}-\d{2}-\d{2}$/.test(date) ? date : prev.expenseDate,
        receiptNumber: (f["receiptNumber"] as string) || prev.receiptNumber,
      }));
    } catch (e: unknown) {
      setError(errMsg(e) ?? "Could not read the receipt. Fill the fields manually.");
      throw e;
    }
  }

  // Runs the receipt parse with the "Reading…" state; swallows the error (shown via setError).
  async function runReceiptParse(file: File) {
    setParsingReceipt(true);
    try { await autofillFromReceipt(file); } catch { /* error already surfaced */ }
    finally { setParsingReceipt(false); }
  }

  // Top-of-items entry point: the user first chooses a receipt (pendingReceipt), then
  // clicks the button to read it. That opens a fresh, pre-filled item form the user
  // reviews/verifies before saving; the same file is uploaded to S3 with the item.
  async function readPendingReceipt() {
    if (!selected || !pendingReceipt) throw new Error("validation");
    setEditingItem(null);
    setItemForm({ ...BLANK_ITEM, currency: selected.currency });
    setReceiptFile(pendingReceipt);
    setShowAddItem(true);
    setError("");
    await runReceiptParse(pendingReceipt);
    setPendingReceipt(null);
  }

  async function saveItem() {
    if (!selected) throw new Error("validation");
    setSaving(true); setError("");
    try {
      const { approvedAmount: approvedRaw, paymentMode, ...form } = itemForm;
      // Only Finance sets the approved amount, and only on an existing item.
      let approvedAmount: number | null | undefined;
      if (isFinance && editingItem) {
        approvedAmount = approvedRaw.trim() === "" ? null : Number(approvedRaw);
        if (approvedAmount !== null && !(approvedAmount >= 0)) {
          setError("Approved amount must be a number.");
          throw new Error("validation");
        }
      }
      const payload = {
        ...form,
        ...(paymentMode ? { paymentMode } : {}),
        ...(approvedAmount !== undefined ? { approvedAmount } : {}),
        amount: parseFloat(itemForm.amount),
        billableMarkup: itemForm.billableMarkup ? parseFloat(itemForm.billableMarkup) : undefined,
        noReceiptReason: itemForm.noReceipt && itemForm.noReceiptReason ? itemForm.noReceiptReason : undefined,
        receiptNumber: itemForm.receiptNumber?.trim() || undefined,
      };
      let itemId = editingItem?.id;
      if (editingItem) {
        await api.patch(`/expenses/${selected.id}/items/${editingItem.id}`, payload);
      } else {
        const res = await api.post<ExpenseItem>(`/expenses/${selected.id}/items`, payload);
        itemId = res.data.id;
      }
      if (!itemForm.noReceipt && receiptFile && itemId) {
        await uploadReceipt(selected.id, itemId, receiptFile);
      }
      setShowAddItem(false);
      setEditingItem(null);
      setItemForm({ ...BLANK_ITEM });
      setReceiptFile(null);
      setPendingReceipt(null);
      setReceiptInfo("");
      openReport(selected.id);
    } catch (e: unknown) {
      setError(errMsg(e) ?? "Failed");
      throw e;
    } finally { setSaving(false); }
  }

  async function deleteItem(itemId: string) {
    if (!selected) return;
    const ok = await confirm({
      title: "Delete this expense item?",
      description: "This item will be permanently removed. This cannot be undone.",
      actionText: "Delete",
      tone: "destructive",
    });
    if (!ok) return;
    try {
      await api.delete(`/expenses/${selected.id}/items/${itemId}`);
      openReport(selected.id);
    } catch (e: unknown) {
      alert(errMsg(e) ?? "Failed to delete item");
    }
  }

  async function openVerifyReview(item: ExpenseItem, kind: "manager" | "finance", queue: ExpenseItem[]) {
    setVerifyKind(kind);
    setVerifyQueue(queue);
    setVerifyReviewItem(item);
    setVerifyApproved(String(payable(item)));
    setVerifyChecked(false);
    setVerifyReceiptUrl(null);
    if (item.receiptS3Key) {
      try {
        const r = await api.get<{ url: string }>(`/expenses/items/${item.id}/receipt`);
        setVerifyReceiptUrl(r.data.url);
      } catch (e: unknown) { logError("ExpensesPage.openVerifyReview", e); }
    }
  }

  function closeVerifyReview() {
    setVerifyReviewItem(null);
    setVerifyQueue([]);
    setVerifyKind(null);
    setVerifyChecked(false);
    setVerifyReceiptUrl(null);
  }

  async function confirmVerify() {
    if (!selected || !verifyReviewItem || !verifyKind) throw new Error("validation");
    setVerifySaving(true);
    try {
      if (verifyKind === "manager") {
        await api.patch(`/expenses/${selected.id}/items/${verifyReviewItem.id}/manager-verify`);
      } else {
        const approved = Number(verifyApproved);
        const claimed = Number(verifyReviewItem.amount);
        if (verifyApproved.trim() === "" || !(approved >= 0) || approved > claimed) {
          alert("Approved amount must be between 0 and the claimed amount.");
          throw new Error("validation");
        }
        // Approving the full claim stores nothing extra; a lower figure is what gets paid.
        await api.patch(`/expenses/${selected.id}/items/${verifyReviewItem.id}/verify`,
          { approvedAmount: approved === claimed ? null : approved });
      }
      const rest = verifyQueue.filter(i => i.id !== verifyReviewItem.id);
      if (rest.length > 0) {
        await openVerifyReview(rest[0]!, verifyKind, rest);
      } else {
        closeVerifyReview();
      }
      await openReport(selected.id);
    } catch (e: unknown) {
      alert(errMsg(e) ?? "Failed to verify item");
      throw e;
    } finally {
      setVerifySaving(false);
    }
  }

  function startEditReport(r: ExpenseReport) {
    setEditingReportId(r.id);
    setNewReportForm({ title: r.title, currency: r.currency });
    setError("");
    setShowNewReport(true);
  }

  async function deleteReport(id: string) {
    const ok = await confirm({
      title: "Delete this expense report?",
      description: "The report and all its items will be permanently deleted. This cannot be undone.",
      actionText: "Delete",
      tone: "destructive",
    });
    if (!ok) return;
    try {
      await api.delete(`/expenses/${id}`);
      load();
    } catch (e: unknown) {
      alert(errMsg(e) ?? "Failed to delete report");
    }
  }

  async function submitReport() {
    if (!selected) throw new Error("validation");
    try {
      await api.patch(`/expenses/${selected.id}/submit`);
      setSelected(null); load();
    } catch (e: unknown) {
      alert(errMsg(e) ?? "Failed");
      throw e;
    }
  }

  function openActionDialog(mode: "manager" | "finance" | "reject") {
    setRemarkInput("");
    setActionDialog(mode);
  }

  async function confirmAction() {
    if (!selected || !actionDialog) throw new Error("validation");
    if (actionDialog === "reject" && !remarkInput.trim()) throw new Error("validation");
    try {
      if (actionDialog === "reject") {
        await api.patch(`/expenses/${selected.id}/reject`, { reason: remarkInput.trim() });
      } else {
        const url = actionDialog === "manager" ? `/expenses/${selected.id}/approve` : `/expenses/${selected.id}/finance-approve`;
        await api.patch(url, { remark: remarkInput.trim() || undefined });
      }
      setActionDialog(null);
      openReport(selected.id); load();
    } catch (e: unknown) {
      alert(errMsg(e) ?? "Failed");
      throw e;
    }
  }

  if (isUS) return <ManagedInADP module="Expenses" />;
  return (
    <div className="px-8 pb-8 pt-2">
      <div className="mb-3 flex items-center justify-between">
        <h1 className="text-[22px] font-bold text-[color:var(--color-text-900)]">Expenses</h1>
        {tab !== "report" && tab !== "pettycash" && (
          <Button onClick={() => { setShowNewReport(true); setEditingReportId(null); setNewReportForm({ ...BLANK_REPORT }); setError(""); }}>
            New Report
          </Button>
        )}
      </div>

      <PanelReveal open={showNewReport}>
          <GlowCard
            size="none"
            glow="ultra"
            spotlight="off"
            // The sweep replays when the panel switches between create and edit.
            interaction="both"
            sweepKey={editingReportId ?? "new"}
            className="mb-6 p-5"
          >
          <h3 style={{ marginBottom: 16, fontWeight: 600, fontSize: 15 }}>{editingReportId ? "Edit Expense Report" : "Create Expense Report"}</h3>
          <div style={{ display: "grid", gridTemplateColumns: "3fr 1fr", gap: 12, marginBottom: 12 }}>
            <div>
              <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 4 }}>Title</label>
              <input style={inputStyle} value={newReportForm.title}
                onChange={e => setNewReportForm(f => ({ ...f, title: e.target.value }))} placeholder="e.g. Client Visit — Bangalore May 2026" />
            </div>
            <div>
              <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 4 }}>Currency</label>
              <DropdownSelect
                value={newReportForm.currency}
                onChange={v => setNewReportForm(f => ({ ...f, currency: v }))}
                options={CURRENCY_OPTIONS}
                placeholder="INR"
                ariaLabel="Currency"
                clearable={false}
                className="w-full justify-between"
                open={openFilter === "newReportCurrency"}
                onToggle={() => setOpenFilter(f => (f === "newReportCurrency" ? null : "newReportCurrency"))}
                onClose={() => setOpenFilter(f => (f === "newReportCurrency" ? null : f))}
              />
            </div>
          </div>
          {error && <p style={{ color: "var(--color-error)", fontSize: 13, marginBottom: 8 }}>{error}</p>}
          <div style={{ display: "flex", gap: 8 }}>
            <AsyncButton
              onClick={() => saveReport()}
              disabled={saving || !newReportForm.title}
              confirm={editingReportId ? {
                title: "Save these changes?",
                description: "Updates this report's title and currency. Its items are not affected.",
                actionText: "Save Changes",
              } : {
                title: "Create this expense report?",
                description: `Creates a draft report in ${newReportForm.currency}. You add items to it before submitting.`,
                actionText: "Create",
              }}
            >
              {editingReportId ? "Save" : "Create"}
            </AsyncButton>
            <Button variant="secondary" onClick={() => { setShowNewReport(false); setEditingReportId(null); }}>
              Cancel
            </Button>
          </div>
          </GlowCard>
      </PanelReveal>

      <Tabs value={tab} onValueChange={v => { setTab(v as typeof tab); setView("pending"); setPage(1); setFilterStatuses(new Set()); setFilterEmployeeId(""); setFilterPeriod("all"); setFilterFrom(""); setFilterTo(""); }} className="mb-4">
        <TabsList>
          <TabsTrigger value="mine">My Expense</TabsTrigger>
          {canViewTeam && <TabsTrigger value="team">Team Expense</TabsTrigger>}
          {canViewReport && <TabsTrigger value="report">Report</TabsTrigger>}
          {canViewPettyCash && <TabsTrigger value="pettycash">Petty Cash Book</TabsTrigger>}
        </TabsList>
      </Tabs>

      {tab === "report" ? (
        <Suspense fallback={<p className="py-16 text-center text-[13px] text-surface-400">Loading analytics…</p>}>
          <ExpenseReportAnalytics />
        </Suspense>
      ) : tab === "pettycash" ? (
        <Suspense fallback={<p className="py-16 text-center text-[13px] text-surface-400">Loading petty cash book…</p>}>
          <PettyCashBook role={userRole} employeeId={employeeId} refreshKey={pettyCashRefresh} onOpenReport={id => void openReport(id)} />
        </Suspense>
      ) : (
      <>
      <Tabs value={view} onValueChange={v => { setView(v as typeof view); setPage(1); setFilterStatuses(new Set()); }} className="mb-4">
        <TabsList>
          <TabsTrigger value="pending" className="px-3 py-1.5 text-[13px]">Pending</TabsTrigger>
          <TabsTrigger value="all" className="px-3 py-1.5 text-[13px]">All Requests</TabsTrigger>
        </TabsList>
      </Tabs>
      <div style={{ marginBottom: 16, display: "flex", gap: 8, flexWrap: "wrap" }}>
        <Input
          type="text"
          placeholder="Search by title or employee…"
          value={search}
          onChange={e => onSearchChange(e.target.value)}
          className="max-w-55"
        />
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {tab === "team" && (isHR || isFinance) && (
            <Combobox
              value={filterEmployeeId}
              onValueChange={v => { setFilterEmployeeId(v); setPage(1); }}
              options={employeeOptions}
              placeholder="All employees"
              searchPlaceholder="Search name, code or email…"
              className="w-52"
            />
          )}
          <DropdownSelect
            value={filterPeriod}
            onChange={v => { setFilterPeriod(v as typeof filterPeriod); setPage(1); }}
            options={EXPORT_PERIOD_OPTIONS}
            placeholder="All time"
            ariaLabel="Period"
            clearable={false}
            open={openFilter === "filterPeriod"}
            onToggle={() => setOpenFilter(f => (f === "filterPeriod" ? null : "filterPeriod"))}
            onClose={() => setOpenFilter(f => (f === "filterPeriod" ? null : f))}
          />
          <div className="relative">
            <DropdownButton expanded={openFilter === "status"} onClick={() => setOpenFilter(f => (f === "status" ? null : "status"))}>
              {filterStatuses.size === 0 ? "All statuses" : `Status (${filterStatuses.size})`}
            </DropdownButton>
            {openFilter === "status" && (
              <DropdownPanel align="end" className="min-w-[200px]" onClose={() => setOpenFilter(f => (f === "status" ? null : f))}>
                <MenuList className="p-2">
                  <DropdownCheckboxRow checked={filterStatuses.size === 0} onChange={() => { setFilterStatuses(new Set()); setPage(1); }}>
                    All statuses
                  </DropdownCheckboxRow>
                  {statusFilterOptions.map(o => (
                    <DropdownCheckboxRow key={o.value} checked={filterStatuses.has(o.value)} onChange={() => toggleFilterStatus(o.value)}>
                      {o.label}
                    </DropdownCheckboxRow>
                  ))}
                </MenuList>
              </DropdownPanel>
            )}
          </div>
          {tab === "team" && (isHR || isFinance) && (
            <SpecularButton size="lg" className="h-9 gap-1.5 px-3" onClick={openExportDialog}
              title="Export reimbursement detail to Excel">
              <Download size={14} /> Export Excel
            </SpecularButton>
          )}
        </div>
      </div>

      {filterPeriod !== "all" && (
        <div className="mb-4 flex flex-wrap items-center gap-2">
          {filterPeriod === "month" && (
            <>
              <DropdownSelect value={filterMonth} onChange={v => { setFilterMonth(v); setPage(1); }} options={EXPORT_MONTH_OPTIONS} placeholder="Month" ariaLabel="Filter month" clearable={false}
                open={openFilter === "filterMonth"} onToggle={() => setOpenFilter(f => (f === "filterMonth" ? null : "filterMonth"))} onClose={() => setOpenFilter(f => (f === "filterMonth" ? null : f))} />
              <DropdownSelect value={filterYear} onChange={v => { setFilterYear(v); setPage(1); }} options={EXPORT_YEAR_OPTIONS} placeholder="Year" ariaLabel="Filter year" clearable={false}
                open={openFilter === "filterYearM"} onToggle={() => setOpenFilter(f => (f === "filterYearM" ? null : "filterYearM"))} onClose={() => setOpenFilter(f => (f === "filterYearM" ? null : f))} />
            </>
          )}
          {filterPeriod === "year" && (
            <DropdownSelect value={filterYear} onChange={v => { setFilterYear(v); setPage(1); }} options={EXPORT_YEAR_OPTIONS} placeholder="Year" ariaLabel="Filter year" clearable={false}
              open={openFilter === "filterYear"} onToggle={() => setOpenFilter(f => (f === "filterYear" ? null : "filterYear"))} onClose={() => setOpenFilter(f => (f === "filterYear" ? null : f))} />
          )}
          {filterPeriod === "custom" && (
            <>
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-[color:var(--color-text-500)]">From</span>
                <div className="w-36"><DatePicker style={inputStyle} value={filterFrom} max={filterTo || undefined} onChange={v => { setFilterFrom(v); setPage(1); }} /></div>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-[color:var(--color-text-500)]">To</span>
                <div className="w-36"><DatePicker style={inputStyle} value={filterTo} min={filterFrom || undefined} onChange={v => { setFilterTo(v); setPage(1); }} /></div>
              </div>
            </>
          )}
        </div>
      )}

      <div style={{ background: "var(--color-surface-0)", borderRadius: "var(--radius-lg)", border: "1px solid var(--color-surface-200)", overflow: "hidden" }}>
        <Table className="text-sm">
          <TableHeader>
            <TableRow>
              {["Title", "Employee", "Created At", "Items", "Amount", "Status", "Remark", "Details", "Action"].map(h => (
                <TableHead key={h} className="px-3.5 py-2.5">{h}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={9} className="px-3.5 py-9 text-center text-[13px] text-surface-400">Loading…</TableCell></TableRow>
            ) : reports.length === 0 ? (
              <TableRow><TableCell colSpan={9} className="px-3.5 py-9 text-center text-[13px] text-surface-400">No expense reports found</TableCell></TableRow>
            ) : reports.map(r => (
              <TableRow key={r.id} className="transition-colors hover:bg-surface-50">
                <TableCell className="px-3.5 py-2.5 font-semibold">{r.title}</TableCell>
                <TableCell className="px-3.5 py-2.5">{r.employee.firstName} {r.employee.lastName}</TableCell>
                <TableCell className="whitespace-nowrap px-3.5 py-2.5 tabular-nums">{formatTimestampMDY(r.createdAt)}</TableCell>
                <TableCell className="px-3.5 py-2.5 tabular-nums">{r._count.items}</TableCell>
                <TableCell className="whitespace-nowrap px-3.5 py-2.5 font-semibold tabular-nums">{Number(r.totalAmount).toLocaleString()} {r.currency}</TableCell>
                <TableCell className="px-3.5 py-2.5">
                  <div className="flex flex-wrap items-center gap-1">
                    <Badge tone={statusTone(r.status)} className="whitespace-nowrap">{statusLabel(r.status)}</Badge>
                    {r.status === "SUBMITTED" && tab === "team" && (
                      r.escalatedAt
                        ? <Badge tone="error" className="whitespace-nowrap">Escalated</Badge>
                        // Only a missed deadline is flagged; the due date itself is not shown.
                        : r.approvalDueAt && new Date(r.approvalDueAt) < new Date()
                          ? <Badge tone="error" className="whitespace-nowrap">Overdue</Badge>
                          : null
                    )}
                    {r.managerApprovedLate && <Badge tone="error" className="whitespace-nowrap">Approved late</Badge>}
                  </div>
                </TableCell>
                <TableCell className="max-w-55 whitespace-pre-wrap break-words px-3.5 py-2.5 text-xs text-surface-600">
                  {r.remark ?? "—"}
                </TableCell>
                <TableCell className="px-3.5 py-2.5">
                  <Button onClick={() => openReport(r.id)} variant="ghost" size="sm"
                    className="h-auto px-0 text-brand-600 hover:bg-transparent hover:underline">
                    View →
                  </Button>
                </TableCell>
                <TableCell className="px-3.5 py-2.5">
                  {isHR || isFinance || (tab === "mine" && (r.status === "DRAFT" || r.status === "REJECTED")) ? (
                    <div style={{ display: "flex", gap: 2, alignItems: "center" }}>
                      <Button onClick={() => startEditReport(r)} title="Edit report" variant="ghost" size="icon" className="text-surface-500">
                        <Pencil size={14} />
                      </Button>
                      <Button onClick={() => deleteReport(r.id)} title="Delete report" variant="ghost" size="icon" className="text-error">
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  ) : (
                    <span style={{ color: "var(--color-text-400)", fontSize: 12 }}>—</span>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {!loading && reports.length > 0 && <Pagination meta={meta} onPageChange={setPage} />}
      </>
      )}

      {selected && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}
          onClick={() => setSelected(null)}>
          <div style={{ background: "var(--color-surface-0)", borderRadius: "var(--radius-lg)", padding: 20, width: "80vw", height: "85vh", maxWidth: "80vw", maxHeight: "85vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.35)" }}
            onClick={e => e.stopPropagation()}>
            {detailLoading ? (
              <p style={{ color: "var(--color-text-400)", fontSize: 14 }}>Loading…</p>
            ) : (
              <>
                <div className="mb-3.5 flex items-center justify-between gap-3">
                  <div className="flex min-w-0 flex-wrap items-center gap-x-2.5 gap-y-1">
                    <h3 className="truncate text-lg font-bold">{selected.title}</h3>
                    <Badge tone={statusTone(selected.status)}>{statusLabel(selected.status)}</Badge>
                  </div>
                  <Button onClick={() => setSelected(null)} variant="ghost" size="icon" className="shrink-0 text-text-400"><X size={16} /></Button>
                </div>

                <div className="mb-5 grid grid-cols-[repeat(auto-fit,minmax(230px,1fr))] gap-x-5 gap-y-2.5 rounded-md border border-surface-200 bg-surface-50 px-4 py-3 sm:divide-x sm:divide-surface-200">
                  {([
                    { label: "Submitted By", value: `${selected.employee.firstName} ${selected.employee.lastName}`, strong: false },
                    { label: "Employee Code", value: selected.employee.employeeCode, strong: false },
                    { label: "Payable Total", value: `${Number(selected.totalAmount).toLocaleString()} ${selected.currency}${claimedTotal !== Number(selected.totalAmount) ? ` (claimed ${claimedTotal.toLocaleString()})` : ""}`, strong: true },
                  ]).map((f, i) => (
                    <div key={f.label} className={cn("flex min-w-0 items-baseline gap-2", i > 0 && "sm:pl-5")}>
                      <span className="shrink-0 text-[10px] font-bold uppercase tracking-[0.05em] text-[color:var(--color-text-500)]">{f.label}</span>
                      <span className={cn(
                        "truncate font-semibold text-[color:var(--color-text-800)]",
                        f.strong ? "text-[15px] font-bold tabular-nums" : "text-sm"
                      )}>{f.value}</span>
                    </div>
                  ))}
                </div>

                {selected.status === "SUBMITTED" && isFinance && !canApproveManager && (
                  <div style={{ background: "var(--color-info-bg)", color: "var(--color-info-fg)", border: "1px solid var(--color-surface-200)", borderRadius: "var(--radius-md)", padding: "10px 14px", marginBottom: 20, fontSize: 13 }}>
                    Manager approval is pending. You'll be able to verify items and approve this report once the manager has approved it.
                  </div>
                )}

                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 20 }}>
                  {(selected.status === "DRAFT" || selected.status === "REJECTED") && (
                    <>
                      {/* Opens the item form in place -- a view control, not an API call. h-9 lines it up with the AsyncButton beside it. */}
                      <SpecularButton size="lg" className="h-9"
                        onClick={() => { setEditingItem(null); setItemForm({ ...BLANK_ITEM, currency: selected.currency }); setShowAddItem(true); setError(""); setReceiptFile(null); setPendingReceipt(null); setReceiptInfo(""); }}>
                        + Add Item
                      </SpecularButton>
                      <AsyncButton
                        onClick={() => submitReport()}
                        disabled={selected.items.length === 0}
                        title="Submit for manager approval"
                        confirm={{
                          title: "Submit this report for approval?",
                          description: `${selected.items.length} item${selected.items.length === 1 ? "" : "s"} totalling ${Number(selected.totalAmount).toLocaleString()} ${selected.currency} goes to your manager. You can't add or edit items while it is under review.`,
                          actionText: "Submit",
                        }}
                      >
                        Submit
                      </AsyncButton>
                    </>
                  )}
                  {selected.status === "SUBMITTED" && canApproveManager && (!isOwnReport || isPrivilegedApprover) && (
                    <Button variant="success" onClick={() => openActionDialog("manager")} disabled={selected.items.some(i => !i.managerVerified)}
                      title="Approve as manager and send to Finance for the final approval">
                      Approve &amp; Send to Finance
                    </Button>
                  )}
                  {selected.status === "SUBMITTED" && canApproveManager && !isOwnReport && (
                    <Button variant="destructive" onClick={() => openActionDialog("reject")}>
                      Reject
                    </Button>
                  )}
                  {selected.status === "MANAGER_APPROVED" && canApproveFinance && (!isOwnReport || isPrivilegedApprover) && (
                    <Button variant="success" onClick={() => openActionDialog("finance")} disabled={selected.items.some(i => !i.financeVerified)}
                      title="Give this report its final approval">
                      Final Approve
                    </Button>
                  )}
                  {selected.status === "MANAGER_APPROVED" && canApproveFinance && !isOwnReport && (
                    <Button variant="destructive" onClick={() => openActionDialog("reject")}>
                      Reject
                    </Button>
                  )}
                </div>

                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                  <h4 style={{ fontWeight: 600, fontSize: 13, color: "var(--color-text-600)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Items ({selected.items.length})</h4>
                  {isFinance && selected.status === "MANAGER_APPROVED" && selected.items.some(i => !i.financeVerified) && (
                    <Button size="sm" onClick={() => {
                        const unverified = selected.items.filter(i => !i.financeVerified);
                        openVerifyReview(unverified[0]!, "finance", unverified);
                      }}>
                      Verify All
                    </Button>
                  )}
                  {canManagerVerify && selected.items.some(i => !i.managerVerified) && (
                    <Button size="sm" onClick={() => {
                        const unverified = selected.items.filter(i => !i.managerVerified);
                        openVerifyReview(unverified[0]!, "manager", unverified);
                      }}>
                      Verify All
                    </Button>
                  )}
                </div>
                <div className="overflow-hidden rounded-lg border border-surface-200 bg-surface-0">
                {selected.items.length === 0 ? (
                  <p className="px-3.5 py-9 text-center text-[13px] text-surface-400">No items yet. Add items before submitting.</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        {["Date","Expense Type","Description","Mode","Amount","Approved","Billable","Receipt",
                          ...(showManagerVerifyColumn ? ["Manager Verify"] : []),
                          ...(showFinanceVerifyColumn ? ["Finance Verify"] : []),
                          ...(canEditItems ? ["Actions"] : [])].map(h => (
                          <TableHead key={h}>{h}</TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selected.items.map(item => (
                        <TableRow key={item.id} className="transition-colors hover:bg-surface-50">
                          <TableCell className="whitespace-nowrap px-3 py-2.5 tabular-nums">{formatDateMDY(item.expenseDate)}</TableCell>
                          <TableCell className="px-3 py-2.5">
                            <Badge tone="neutral">{expenseTypeLabel(item.category)}</Badge>
                          </TableCell>
                          <TableCell className="px-3 py-2.5 text-[color:var(--color-text-600)]">
                            {item.description}
                            {item.receiptNumber && <div className="text-[11px] text-[color:var(--color-text-400)]">#{item.receiptNumber}</div>}
                          </TableCell>
                          <TableCell className="whitespace-nowrap px-3 py-2.5">{paymentModeLabel(item.paymentMode)}</TableCell>
                          <TableCell className="whitespace-nowrap px-3 py-2.5 font-semibold tabular-nums">{Number(item.amount).toLocaleString()} {item.currency}</TableCell>
                          <TableCell className={cn("whitespace-nowrap px-3 py-2.5 tabular-nums", item.approvedAmount != null && payable(item) < Number(item.amount) && "font-semibold text-error")}>
                            {item.approvedAmount == null ? "—" : `${payable(item).toLocaleString()} ${item.currency}`}
                          </TableCell>
                          <TableCell className="px-3 py-2.5">
                            {item.billableToClient ? <span className="font-semibold text-brand-600">Yes{item.billableMarkup ? ` +${item.billableMarkup}%` : ""}</span> : <span className="text-[color:var(--color-text-400)]">No</span>}
                          </TableCell>
                          <TableCell className="px-3 py-2.5">
                            {item.receiptS3Key ? (
                              <div style={{ display: "flex", gap: 2 }}>
                                <Button onClick={() => viewReceipt(item.id)} variant="ghost" size="icon" className="text-brand-600" title="View receipt">
                                  <Eye size={14} />
                                </Button>
                                <Button onClick={() => downloadReceipt(item.id)} variant="ghost" size="icon" className="text-brand-600" title="Download receipt">
                                  <Download size={14} />
                                </Button>
                              </div>
                            ) : item.noReceipt ? (
                              <span style={{ color: "var(--color-text-400)", fontSize: 12 }}>No receipt{item.noReceiptReason ? ` — ${item.noReceiptReason}` : ""}</span>
                            ) : (selected.status === "DRAFT" || selected.status === "REJECTED") ? (
                              uploadingReceipt === item.id ? (
                                <span style={{ fontSize: 12, color: "var(--color-text-400)" }}>Uploading…</span>
                              ) : (
                                <FileInput variant="button" size="sm" accept="image/*,.pdf" placeholder="Add receipt" className="h-7 w-[112px] px-2" value={null}
                                  onChange={async file => {
                                    if (!file) return;
                                    await uploadReceipt(selected.id, item.id, file);
                                    openReport(selected.id);
                                  }} />
                              )
                            ) : (
                              <span style={{ color: "var(--color-text-400)", fontSize: 12 }}>—</span>
                            )}
                          </TableCell>
                          {showManagerVerifyColumn && (
                            <TableCell className="px-3 py-2.5">
                              {item.managerVerified ? (
                                <span style={{ color: "var(--color-success)", fontSize: 12, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 4 }}>
                                  <Check size={14} /> Verified
                                </span>
                              ) : canManagerVerify ? (
                                <Button size="sm" onClick={() => openVerifyReview(item, "manager", [item])}>
                                  Verify
                                </Button>
                              ) : (
                                <span style={{ color: "var(--color-text-400)", fontSize: 12 }}>Pending</span>
                              )}
                            </TableCell>
                          )}
                          {showFinanceVerifyColumn && (
                            <TableCell className="px-3 py-2.5">
                              {item.financeVerified ? (
                                <span style={{ color: "var(--color-success)", fontSize: 12, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 4 }}>
                                  <Check size={14} /> Verified
                                </span>
                              ) : isFinance && selected.status === "MANAGER_APPROVED" ? (
                                <Button size="sm" onClick={() => openVerifyReview(item, "finance", [item])}>
                                  Verify
                                </Button>
                              ) : selected.status === "MANAGER_APPROVED" ? (
                                <span style={{ color: "var(--color-text-400)", fontSize: 12 }}>Pending</span>
                              ) : (
                                <span style={{ color: "var(--color-text-400)", fontSize: 12 }}>—</span>
                              )}
                            </TableCell>
                          )}
                          {canEditItems && (
                            <TableCell className="whitespace-nowrap px-3 py-2.5">
                              <div style={{ display: "flex", gap: 2 }}>
                                <Button onClick={() => {
                                    setEditingItem(item);
                                    setItemForm({
                                      category: item.category, paymentMode: item.paymentMode ?? "",
                                      approvedAmount: item.approvedAmount == null ? "" : String(Number(item.approvedAmount)),
                                      description: item.description, amount: String(item.amount),
                                      currency: selected.currency, expenseDate: item.expenseDate.slice(0, 10),
                                      billableToClient: item.billableToClient, billableMarkup: item.billableMarkup ?? "",
                                      noReceipt: item.noReceipt, noReceiptReason: item.noReceiptReason ?? "",
                                      receiptNumber: item.receiptNumber ?? "",
                                    });
                                    setShowAddItem(true); setError("");
                                  }}
                                  variant="ghost" size="icon" className="text-surface-500" title="Edit item">
                                  <Pencil size={14} />
                                </Button>
                                <Button onClick={() => deleteItem(item.id)} variant="ghost" size="icon" className="text-error" title="Delete item">
                                  <Trash2 size={14} />
                                </Button>
                              </div>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
                </div>

                {selected.remark && (
                  <div style={{ background: selected.status === "REJECTED" ? "var(--color-error-bg)" : "var(--color-surface-50)", border: `1px solid ${selected.status === "REJECTED" ? "var(--color-error-border)" : "var(--color-surface-200)"}`, borderRadius: "var(--radius-md)", padding: "10px 14px", marginTop: 20, fontSize: 13 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: selected.status === "REJECTED" ? "var(--color-error)" : "var(--color-text-500)", marginBottom: 3 }}>
                      {selected.status === "REJECTED" ? "Rejection Reason" : "Manager Remark"}
                    </div>
                    <div style={{ color: "var(--color-text-800)", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{selected.remark}</div>
                  </div>
                )}

                <PanelReveal open={showAddItem} ref={addItemPanelRef} className="scroll-mt-2">
                  <GlowCard
                    size="none"
                    glow="ultra"
                    spotlight="off"
                    // The sweep replays when the panel switches between add and edit.
                    interaction="both"
                    sweepKey={editingItem?.id ?? "new"}
                    className="mt-5 p-4"
                  >
                    <h4 style={{ fontWeight: 600, marginBottom: 12, fontSize: 14 }}>{editingItem ? "Edit Expense Item" : "Add Expense Item"}</h4>
                    {!editingItem && (
                      <div className="group mb-3.5 flex items-center gap-3 rounded-md border border-dashed border-brand-600 bg-surface-0 px-3.5 py-2.5 transition-colors duration-150 hover:border-brand-700 hover:bg-brand-50">
                        {/* Drop-zone hover: the dashed border brightens and the surface tints,
                            so the whole banner reads as the click target it is.
                            Whole left area is one clickable label → opens the file chooser;
                            the action button is a sibling so it doesn't re-open the picker. */}
                        <label style={{ display: "flex", alignItems: "center", gap: 12, flex: 1, minWidth: 0, cursor: "pointer" }}>
                          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-brand-100 text-brand-600 transition-colors duration-150 group-hover:bg-brand-200">
                            <Upload size={16} />
                          </span>
                          <span style={{ flex: 1, minWidth: 0 }}>
                            <span style={{ display: "block", fontSize: 13, fontWeight: 600, color: "var(--color-text-800)" }}>
                              Upload a receipt to auto-fill
                            </span>
                            <span style={{ display: "block", fontSize: 11, color: "var(--color-text-500)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {pendingReceipt ? pendingReceipt.name : "Reads amount, date, category & description — review before saving."}
                            </span>
                          </span>
                          {/* Inside the label, so it must not be a real <button> (that would
                              swallow the click instead of opening the picker) -- asChild gives
                              the span the Button shape without the element. */}
                          <Button asChild variant="outline" size="sm" className="shrink-0">
                            <span>{pendingReceipt ? "Change" : "Choose file"}</span>
                          </Button>
                          <input type="file" accept="image/*,.pdf" style={{ display: "none" }}
                            onChange={e => { const file = e.target.files?.[0] ?? null; if (file) setPendingReceipt(file); e.target.value = ""; }} />
                        </label>
                        {pendingReceipt && (
                          <AsyncButton onClick={() => readPendingReceipt()} disabled={parsingReceipt}>
                            Read receipt
                          </AsyncButton>
                        )}
                      </div>
                    )}
                    {!editingItem && receiptInfo && (
                      <p style={{ fontSize: 11, color: "var(--color-info-fg)", marginBottom: 10 }}>{receiptInfo}</p>
                    )}
                    {/* Same fields and order as the Petty Cash "Daily Expense" form. */}
                    <div className="mb-2.5 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                      <div>
                        <Label>Current Date</Label>
                        <Input value={formatTimestampMDY(new Date().toISOString())} readOnly disabled />
                      </div>
                      <div>
                        <Label>Transaction Date *</Label>
                        <DateInput value={itemForm.expenseDate} isUS={isUS}
                          min={currentQuarterStartISO()}
                          max={new Date().toISOString().slice(0, 10)}
                          onChange={v => setItemForm(f => ({ ...f, expenseDate: v }))} />
                        <p className="mt-0.5 text-[11px] text-surface-400">Claims for a closed quarter can no longer be added.</p>
                      </div>
                      <div>
                        <Label>Expense Type *</Label>
                        <DropdownSelect
                          value={itemForm.category}
                          onChange={v => setItemForm(f => ({ ...f, category: v }))}
                          options={expenseCategoryOptions}
                          placeholder="Select type…"
                          ariaLabel="Expense Type"
                          clearable={false}
                          className="w-full justify-between"
                          open={openFilter === "itemCategory"}
                          onToggle={() => setOpenFilter(f => (f === "itemCategory" ? null : "itemCategory"))}
                          onClose={() => setOpenFilter(f => (f === "itemCategory" ? null : f))}
                        />
                      </div>
                      <div>
                        <Label>Amount *</Label>
                        <Input type="number" step="0.01" value={itemForm.amount}
                          onChange={e => setItemForm(f => ({ ...f, amount: e.target.value }))} placeholder="0.00" />
                      </div>
                      <div>
                        <Label>Payment Mode *</Label>
                        <DropdownSelect
                          value={itemForm.paymentMode}
                          onChange={v => setItemForm(f => ({ ...f, paymentMode: v }))}
                          options={paymentModeOptions}
                          placeholder="Select mode…"
                          ariaLabel="Payment Mode"
                          clearable={false}
                          className="w-full justify-between"
                          open={openFilter === "itemMode"}
                          onToggle={() => setOpenFilter(f => (f === "itemMode" ? null : "itemMode"))}
                          onClose={() => setOpenFilter(f => (f === "itemMode" ? null : f))}
                        />
                      </div>
                      <div>
                        <Label>Currency</Label>
                        <DropdownSelect
                          value={itemForm.currency}
                          onChange={v => setItemForm(f => ({ ...f, currency: v }))}
                          options={CURRENCY_OPTIONS}
                          placeholder="INR"
                          ariaLabel="Currency"
                          clearable={false}
                          disabled
                          className="w-full justify-between"
                          open={openFilter === "itemCurrency"}
                          onToggle={() => setOpenFilter(f => (f === "itemCurrency" ? null : "itemCurrency"))}
                          onClose={() => setOpenFilter(f => (f === "itemCurrency" ? null : f))}
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <Label>Approved Amount</Label>
                        <Input inputMode="decimal" disabled={!approvedEditable}
                          value={approvedEditable ? itemForm.approvedAmount : itemForm.approvedAmount || "—"}
                          placeholder={approvedEditable ? itemForm.amount || "0.00" : undefined}
                          onChange={e => setItemForm(f => ({ ...f, approvedAmount: e.target.value.replace(/[^0-9.]/g, "") }))} />
                        <p className="mt-0.5 text-[11px] text-surface-400">
                          Set by Finance when verifying (disabled for new items). Once set, it is what is paid.
                        </p>
                      </div>
                    </div>
                    <div style={{ marginBottom: 10 }}>
                      <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>Description</label>
                      <input style={inputStyle} value={itemForm.description}
                        onChange={e => setItemForm(f => ({ ...f, description: e.target.value }))} placeholder="Details…" />
                    </div>
                    <div style={{ marginBottom: 10 }}>
                      <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>Receipt / Order number <span style={{ color: "var(--color-text-400)" }}>(optional)</span></label>
                      <input style={inputStyle} value={itemForm.receiptNumber}
                        onChange={e => setItemForm(f => ({ ...f, receiptNumber: e.target.value }))} placeholder="e.g. INV-10234 or order #" />
                    </div>
                    <div style={{ display: "flex", gap: 16, alignItems: "center", marginBottom: 10 }}>
                      <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer" }}>
                        <input type="checkbox" checked={itemForm.billableToClient}
                          onChange={e => setItemForm(f => ({ ...f, billableToClient: e.target.checked }))} />
                        Billable to client
                      </label>
                      {itemForm.billableToClient && (
                        <input type="number" step="0.01" style={{ ...inputStyle, width: 120 }} value={itemForm.billableMarkup}
                          onChange={e => setItemForm(f => ({ ...f, billableMarkup: e.target.value }))} placeholder="Markup %" />
                      )}
                    </div>
                    <div style={{ marginBottom: 10 }}>
                      <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer", marginBottom: 6 }}>
                        <input type="checkbox" checked={itemForm.noReceipt}
                          onChange={e => setItemForm(f => ({ ...f, noReceipt: e.target.checked }))} />
                        No receipt available
                      </label>
                      {itemForm.noReceipt ? (
                        <input style={inputStyle} value={itemForm.noReceiptReason}
                          onChange={e => setItemForm(f => ({ ...f, noReceiptReason: e.target.value }))}
                          placeholder="Reason (optional)" />
                      ) : (
                        <div>
                          <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 3 }}>Receipt</label>
                          {editingItem?.receiptS3Key ? (
                            <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
                              <Button onClick={() => viewReceipt(editingItem.id)} variant="ghost" size="icon" className="text-brand-600" title="View receipt">
                                <Eye size={14} />
                              </Button>
                              <Button onClick={() => downloadReceipt(editingItem.id)} variant="ghost" size="icon" className="text-brand-600" title="Download receipt">
                                <Download size={14} />
                              </Button>
                              <span style={{ fontSize: 12, color: "var(--color-text-400)" }}>Uploaded</span>
                            </div>
                          ) : (
                            <>
                              <FileInput variant="button" accept="image/*,.pdf" disabled={parsingReceipt} value={receiptFile}
                                className="flex w-fit"
                                onChange={file => {
                                  setReceiptFile(file);
                                  if (file) void runReceiptParse(file);
                                }} />
                              <p style={{ fontSize: 11, color: "var(--color-text-400)", marginTop: 4 }}>
                                The amount, date, category and description are read from the receipt and filled in below. This file is saved with the item.
                              </p>
                              {parsingReceipt && (
                                <span style={{ fontSize: 12, color: "var(--color-brand-600)" }}>Reading receipt…</span>
                              )}
                              {receiptFile && !parsingReceipt && (
                                <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
                                  <span style={{ fontSize: 12, color: "var(--color-success)" }}>✓ {receiptFile.name} — review the fields, then Save.</span>
                                  <Button variant="ghost" size="sm" onClick={() => void runReceiptParse(receiptFile)}
                                    className="h-auto px-0 text-brand-600 hover:bg-transparent hover:underline">
                                    Re-read
                                  </Button>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                    {error && <p style={{ color: "var(--color-error)", fontSize: 13, marginBottom: 8 }}>{error}</p>}
                    <div style={{ display: "flex", gap: 8 }}>
                      <AsyncButton
                        onClick={() => saveItem()}
                        disabled={saving || !itemForm.description || !itemForm.amount || !itemForm.expenseDate || (!editingItem && !itemForm.paymentMode)}
                      >
                        {editingItem ? "Save" : "Add"}
                      </AsyncButton>
                      <Button variant="secondary" onClick={() => { setShowAddItem(false); setEditingItem(null); }}>
                        Cancel
                      </Button>
                    </div>
                  </GlowCard>
                </PanelReveal>
              </>
            )}
          </div>
        </div>
      )}

      {actionDialog && (
        <Dialog open onOpenChange={open => { if (!open) setActionDialog(null); }}>
          <DialogContent className="max-w-105">
            <DialogHeader>
              <DialogTitle>
                {actionDialog === "reject" ? "Rejection reason"
                  : actionDialog === "manager" ? "Send to Finance — remark (optional)" : "Final approval — remark (optional)"}
              </DialogTitle>
            </DialogHeader>
            <Textarea autoFocus rows={4} resize="vertical"
              value={remarkInput} onChange={e => setRemarkInput(e.target.value)}
              placeholder={actionDialog === "reject" ? "Explain why this report is being rejected…" : "Add a note (optional)…"} />
            <DialogFooter className="justify-start">
              <AsyncButton
                variant={actionDialog === "reject" ? "destructive" : "success"}
                onClick={() => confirmAction()}
                disabled={actionDialog === "reject" && !remarkInput.trim()}
                confirm={actionDialog === "reject" ? {
                  title: "Reject this report?",
                  description: "Sends it back to the employee with your reason. They have to fix it and resubmit.",
                  actionText: "Reject",
                  tone: "destructive",
                } : {
                  title: actionDialog === "manager" ? "Send this report to Finance?" : "Give this report final approval?",
                  description: actionDialog === "manager"
                    ? "You approve it as manager. It is not final yet: the status becomes Pending Finance Approval until Finance approves it."
                    : "Gives the report its final approval and clears it for payout.",
                  actionText: actionDialog === "manager" ? "Send to Finance" : "Final Approve",
                  tone: "success",
                }}
              >
                {actionDialog === "reject" ? "Reject" : actionDialog === "manager" ? "Send to Finance" : "Final Approve"}
              </AsyncButton>
              <Button variant="secondary" onClick={() => setActionDialog(null)}>
                Cancel
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {showExport && (
        <Dialog open onOpenChange={o => { if (!o) setShowExport(false); }}>
          <DialogContent className="max-w-105">
            <DialogHeader>
              <DialogTitle>Export Reimbursements</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="mb-1.5 block text-xs font-medium text-[color:var(--color-text-500)]">Employee</label>
                <Combobox
                  value={exportEmployeeId}
                  onValueChange={setExportEmployeeId}
                  options={employeeOptions}
                  placeholder="All employees"
                  searchPlaceholder="Search name, code or email…"
                  className="w-full"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-medium text-[color:var(--color-text-500)]">Period</label>
                <DropdownSelect
                  value={exportPeriod}
                  onChange={v => setExportPeriod(v as typeof exportPeriod)}
                  options={EXPORT_PERIOD_OPTIONS}
                  placeholder="All time"
                  ariaLabel="Period"
                  clearable={false}
                  className="w-full justify-between"
                  open={openFilter === "exportPeriod"}
                  onToggle={() => setOpenFilter(f => (f === "exportPeriod" ? null : "exportPeriod"))}
                  onClose={() => setOpenFilter(f => (f === "exportPeriod" ? null : f))}
                />
                {exportPeriod === "month" && (
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <DropdownSelect value={exportMonth} onChange={setExportMonth} options={EXPORT_MONTH_OPTIONS} placeholder="Month" ariaLabel="Month" clearable={false} className="w-full justify-between"
                      open={openFilter === "exportMonth"} onToggle={() => setOpenFilter(f => (f === "exportMonth" ? null : "exportMonth"))} onClose={() => setOpenFilter(f => (f === "exportMonth" ? null : f))} />
                    <DropdownSelect value={exportYear} onChange={setExportYear} options={EXPORT_YEAR_OPTIONS} placeholder="Year" ariaLabel="Year" clearable={false} className="w-full justify-between"
                      open={openFilter === "exportYearM"} onToggle={() => setOpenFilter(f => (f === "exportYearM" ? null : "exportYearM"))} onClose={() => setOpenFilter(f => (f === "exportYearM" ? null : f))} />
                  </div>
                )}
                {exportPeriod === "year" && (
                  <div className="mt-2">
                    <DropdownSelect value={exportYear} onChange={setExportYear} options={EXPORT_YEAR_OPTIONS} placeholder="Year" ariaLabel="Year" clearable={false} className="w-full justify-between"
                      open={openFilter === "exportYear"} onToggle={() => setOpenFilter(f => (f === "exportYear" ? null : "exportYear"))} onClose={() => setOpenFilter(f => (f === "exportYear" ? null : f))} />
                  </div>
                )}
                {exportPeriod === "custom" && (
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <div>
                      <label className="mb-1 block text-[11px] text-[color:var(--color-text-400)]">From</label>
                      <DatePicker style={inputStyle} value={exportFrom} max={exportTo || undefined} onChange={setExportFrom} />
                    </div>
                    <div>
                      <label className="mb-1 block text-[11px] text-[color:var(--color-text-400)]">To</label>
                      <DatePicker style={inputStyle} value={exportTo} min={exportFrom || undefined} onChange={setExportTo} />
                    </div>
                  </div>
                )}
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-medium text-[color:var(--color-text-500)]">Status</label>
                <div className="space-y-2">
                  <label className="flex cursor-pointer items-center gap-2 text-sm">
                    <Checkbox checked={exportStatuses.size === 0} onCheckedChange={() => setExportStatuses(new Set())} />
                    All statuses
                  </label>
                  {EXPORT_STATUS_OPTIONS.map(o => (
                    <label key={o.value} className="flex cursor-pointer items-center gap-2 text-sm">
                      <Checkbox checked={exportStatuses.has(o.value)} onCheckedChange={() => toggleExportStatus(o.value)} />
                      {o.label}
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter className="justify-start">
              <AsyncButton onClick={() => downloadReimbursementExcel()}>
                <Download size={14} className="mr-1.5" /> Export Excel
              </AsyncButton>
              <Button variant="secondary" onClick={() => setShowExport(false)}>Cancel</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {verifyReviewItem && (
        // The app's ConfirmationCard *is* this dialog -- the receipt to review
        // and the attestation ride in its content slot, so there is no second
        // confirm step on top. Radix still supplies the focus trap, Escape and
        // aria-modal; DialogContent is stripped back to a transparent frame so
        // the card provides the surface.
        <Dialog open onOpenChange={open => { if (!open) closeVerifyReview(); }}>
          <DialogContent
            ref={verifyPanelRef}
            hideClose
            // Radix focuses the first focusable child on open -- here that is
            // inside the card, which trips its :focus-within flood, so the card
            // appeared already hovered. Focus the frame instead (the same thing
            // ConfirmDialogProvider does): Tab and Escape still work, the card
            // rests until the pointer or keyboard actually reaches it, and a
            // stray Enter can't confirm something unread.
            onOpenAutoFocus={e => { e.preventDefault(); verifyPanelRef.current?.focus(); }}
            className={cn(
              "border-0 bg-transparent p-0 shadow-none",
              verifyReviewItem.receiptS3Key ? "max-w-[880px]" : "max-w-[520px]"
            )}
          >
            <DialogTitle className="sr-only">Verify expense item</DialogTitle>
            <ConfirmationCard
              config={{ ...confirmDialogDefaults, width: 1200 }}
              content={{
                title: `Verify item${verifyQueue.length > 1 ? ` (${verifyQueue.length} remaining)` : ""}`,
                description: `${verifyReviewItem.description} — ${Number(verifyReviewItem.amount).toLocaleString()} ${verifyReviewItem.currency}`,
                actionText: "Verify",
                cancelText: "Cancel",
              }}
              actionDisabled={!verifyChecked || verifySaving}
              // confirmVerify rethrows so AsyncButton call sites can show the
              // failure; this one reports through its own alert, so swallow it.
              onAction={() => { void confirmVerify().catch(() => {}); }}
              onCancel={closeVerifyReview}
            >
              {verifyReviewItem.receiptS3Key ? (
                verifyReceiptUrl ? (
                  /\.(jpe?g|png|gif|webp|bmp|heic)$/i.test(verifyReviewItem.receiptS3Key) ? (
                    <div className="flex max-h-[55vh] min-h-50 w-full items-center justify-center overflow-hidden rounded-md border border-surface-200 bg-surface-100">
                      <img src={verifyReceiptUrl} alt="Receipt" className="max-h-[55vh] max-w-full object-contain" />
                    </div>
                  ) : (
                    <iframe src={verifyReceiptUrl} title="Receipt preview"
                      className="h-[55vh] w-full rounded-md border border-surface-200 bg-surface-100" />
                  )
                ) : (
                  <p>Loading receipt…</p>
                )
              ) : (
                <p>No receipt{verifyReviewItem.noReceiptReason ? ` — ${verifyReviewItem.noReceiptReason}` : ""}</p>
              )}
              {verifyKind === "finance" && (
                <div className="flex flex-wrap items-center gap-2">
                  <Label className="mb-0">Approved Amount ({verifyReviewItem.currency})</Label>
                  <Input type="number" step="0.01" min={0} max={Number(verifyReviewItem.amount)} className="w-40"
                    value={verifyApproved} onChange={e => setVerifyApproved(e.target.value)} />
                  <span className="text-xs text-surface-500">
                    Claimed {Number(verifyReviewItem.amount).toLocaleString()} — lower it to approve less.
                  </span>
                </div>
              )}
              <label className="flex cursor-pointer items-center gap-2">
                <Checkbox checked={verifyChecked} onCheckedChange={c => setVerifyChecked(c === true)} />
                I have reviewed this item and its receipt
              </label>
            </ConfirmationCard>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

